import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import { ViewerItem } from 'presentations/api/schema/user';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { fontSize, fontWeight, spacing } from 'presentations/views/styles';
import { gray04, primary } from 'presentations/views/styles/color';
import { useConversationView } from './hooks';
import MessageList, { MessageListMain } from '../MessageList';
import SubmitForm from '../SubmitForm';
import TextFieldDateRangePicker from 'presentations/views/components/atoms/TextFieldDateRangePicker';
import ChatFormTemplate from '../ChatFormTemplate';

interface Props {
  viewer: ViewerItem;
  onClickBack: () => void;
  defaultContent: string | null;
}

export default function Component(props: Props) {
  const { state, actions } = useConversationView({
    viewer: props.viewer,
    defaultContent: props.defaultContent,
  });
  const t = translateInComponent([translations]);

  return (
    <ChatFormTemplate
      header={
        <Header>
          <Navigation>
            <HeaderLeftItem>
              <HeaderLeftItemIconButton onClick={props.onClickBack}>
                <HeaderLeftItemIcon name="chevron-left" />
              </HeaderLeftItemIconButton>
              <HeaderTitle>
                <ChatTitle>{t(`Title`)}</ChatTitle>
              </HeaderTitle>
            </HeaderLeftItem>
            <HeaderRightItem></HeaderRightItem>
          </Navigation>
          <FilterContainer>
            <RangeContainer>
              <SubTitle>{t('Range')}</SubTitle>
              <TextFieldDateRangePicker
                startedAt={state.startedAtFilter}
                endedAt={state.endedAtFilter}
                onChange={actions.onChangeDateFilter}
                locale={state.userProfile?.locale}
                timeZone={state.userProfile?.timeZone}
                size="medium"
                placeholder={t('DateTime')}
                initializedDateRange={true}
              />
            </RangeContainer>
          </FilterContainer>
        </Header>
      }
      content={
        <MessageListMain>
          <MessageList
            viewer={props.viewer}
            isThinking={state.isLoading}
            messageStream={state.messageStream}
            messages={state.messages}
          />
        </MessageListMain>
      }
      footer={
        <Footer>
          <SubmitForm
            isDisabled={state.isNoContent || state.isLoading}
            isLoading={state.isLoading}
            onChange={actions.onChangeMessage}
            onSubmit={actions.onSubmitMessage}
            content={state.content}
          />
        </Footer>
      }
    />
  );
}
const Header = styled.div``;
const Navigation = styled.div`
  padding: ${spacing(16)} ${spacing(12)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray04};
`;
const FilterContainer = styled.div`
  padding: ${spacing(16)} ${spacing(12)};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${gray04};
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;
const HeaderLeftItem = styled.div`
  display: flex;
  justify-content: flex-start;
`;
const HeaderLeftItemIconButton = styled(IconButton)`
  padding: ${spacing(4)};
  margin-right: ${spacing(8)};
`;
const HeaderLeftItemIcon = styled(Icon)`
  color: ${primary};
`;
const HeaderRightItem = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const ChatTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const Footer = styled.div`
  border-top: 1px solid ${gray04};
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SubTitle = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-right: ${spacing(16)};
  white-space: nowrap;
`;
const Main = styled.div`
  display: flex;
  height: inherit;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
`;
